import React, { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const [validationErrors, setValidationErrors] = useState({});

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Navn er påkrævet';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Telefonnummer er påkrævet';
    } else if (!/^[0-9\s+()-]{8,15}$/.test(formData.phone.trim())) {
      errors.phone = 'Ugyldigt telefonnummer';
    }
    if (!formData.email.trim()) {
      errors.email = 'E-mail er påkrævet';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Ugyldig e-mailadresse';
    }
    if (!formData.description.trim()) {
      errors.description = 'Besked er påkrævet';
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        });
      } else {
        throw new Error('Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der opstod en netværksfejl. Prøv venligst igen.',
      });
    }
  };

  return (
    <div className="bg-warm-sand/20 border border-warm-sand/40 rounded-none p-6 md:p-10 shadow-sm">
      {status.success ? (
        <div className="text-center py-8 animate-in fade-in duration-500">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 mb-4">
            <i className="fa-solid fa-check text-2xl" />
          </div>
          <h3 className="font-display text-2xl text-warm-dark mb-2">Tak for din henvendelse</h3>
          <p className="text-sm text-warm-gray max-w-md mx-auto leading-relaxed">
            Jeg har modtaget din besked og vil vende tilbage til dig så hurtigt som muligt, typisk inden for 1-2 hverdage.
          </p>
          <button
            onClick={() => setStatus({ submitting: false, success: false, error: null })}
            className="mt-6 px-6 py-2.5 bg-warm-dark text-warm-light text-sm font-medium rounded-full hover:bg-warm-gray transition-colors duration-200"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <h3 className="font-display text-2xl text-warm-dark border-b border-warm-sand/30 pb-3">
            Send en besked
          </h3>
          
          {status.error && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-3">
              <i className="fa-solid fa-circle-exclamation text-base" />
              <span>{status.error}</span>
            </div>
          )}

          {/* Honeypot field (absolutely hidden from humans) */}
          <div className="sr-only absolute -z-10 overflow-hidden w-0 h-0">
            <label htmlFor="website_url">Do not fill this field if you are a human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="name" className="text-xs uppercase tracking-wider text-warm-gray font-semibold">
                Navn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-1 py-2.5 bg-transparent border-b rounded-none text-sm text-warm-dark focus:outline-none focus:border-warm-dark transition-all ${
                  validationErrors.name ? 'border-red-400' : 'border-warm-taupe'
                }`}
                placeholder="Dit fulde navn"
              />
              {validationErrors.name && (
                <span className="text-xs text-red-500 mt-1">{validationErrors.name}</span>
              )}
            </div>

            {/* Phone */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="phone" className="text-xs uppercase tracking-wider text-warm-gray font-semibold">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-1 py-2.5 bg-transparent border-b rounded-none text-sm text-warm-dark focus:outline-none focus:border-warm-dark transition-all ${
                  validationErrors.phone ? 'border-red-400' : 'border-warm-taupe'
                }`}
                placeholder="F.eks. 23 36 90 87"
              />
              {validationErrors.phone && (
                <span className="text-xs text-red-500 mt-1">{validationErrors.phone}</span>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="email" className="text-xs uppercase tracking-wider text-warm-gray font-semibold">
              E-mailadresse <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-1 py-2.5 bg-transparent border-b rounded-none text-sm text-warm-dark focus:outline-none focus:border-warm-dark transition-all ${
                validationErrors.email ? 'border-red-400' : 'border-warm-taupe'
              }`}
              placeholder="din.email@eksempel.dk"
            />
            {validationErrors.email && (
              <span className="text-xs text-red-500 mt-1">{validationErrors.email}</span>
            )}
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="description" className="text-xs uppercase tracking-wider text-warm-gray font-semibold">
              Hvordan kan jeg hjælpe dig? <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              rows="5"
              value={formData.description}
              onChange={handleChange}
              className={`w-full px-1 py-2.5 bg-transparent border-b rounded-none text-sm text-warm-dark focus:outline-none focus:border-warm-dark transition-all resize-none ${
                validationErrors.description ? 'border-red-400' : 'border-warm-taupe'
              }`}
              placeholder="Skriv kort om din situation eller dit ønske for terapien..."
            />
            {validationErrors.description && (
              <span className="text-xs text-red-500 mt-1">{validationErrors.description}</span>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status.submitting}
            className="w-full py-4 bg-warm-dark text-warm-light font-medium tracking-wide rounded-full hover:bg-warm-gray disabled:bg-warm-gray/50 transition-all-custom shadow-sm hover:shadow-md flex items-center justify-center gap-2"
          >
            {status.submitting ? (
              <>
                <i className="fa-light fa-spinner-third animate-spin text-lg" />
                <span>Sender besked...</span>
              </>
            ) : (
              <>
                <i className="fa-light fa-paper-plane" />
                <span>Send besked</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
